import { CSSProperties, useCallback, useMemo, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { playSound, Sound } from '~/assets/sounds';
import { Text } from '~/components/ui/Text';
import {
	StyledArrowDown,
	StyledBackWrapper,
	StyledContainer,
	StyledCross,
	StyledItemsContainer,
	StyledSelectItem,
	StyledSelectItemWrapper,
	StyledTopContainer,
} from './Select.styled';

interface ISelectProps {
	items: string[];
	placeholder: string;
	onChange: (value: string | null) => void;
	style?: CSSProperties;
}

const Select: React.FC<ISelectProps> = ({
	items,
	placeholder,
	onChange,
	style,
}: ISelectProps) => {
	const [selected, setSelected] = useState<string | null>(null);
	const [resultsVisible, setResultsVisible] = useState<boolean>(false);

	const scrollbar = useMemo(() => items.length > 3, [items]);

	const handleSelect = useCallback((item: string) => {
		onChange(item);
		setSelected(item);
		setResultsVisible(false);
	}, []);

	const handleClear = useCallback(() => {
		setSelected(null);
		onChange(null);
	}, []);

	return (
		<StyledContainer style={style}>
			<StyledTopContainer
				$selected={!!selected}
				$focus={resultsVisible}
				onClick={() => {
					setResultsVisible((prevState) => !prevState);
					playSound(Sound.ClickButton);
				}}
				onMouseEnter={() => playSound(Sound.LinkCursor)}
			>
				<Text>
					{selected ? (
						selected
					) : (
						<span style={{ opacity: 0.5 }}>{placeholder}</span>
					)}
				</Text>
				{selected ? (
					<StyledCross
						onClick={() => {
							handleClear();
							playSound(Sound.ClickButton);
						}}
					/>
				) : (
					<StyledArrowDown $active={resultsVisible} />
				)}
			</StyledTopContainer>
			<CSSTransition
				in={resultsVisible}
				classNames={'results'}
				timeout={500}
				unmountOnExit
			>
				<StyledItemsContainer>
					{items.map((item, index) => (
						<StyledSelectItemWrapper
							key={index}
							onClick={() => handleSelect(item)}
							$scrollbar={scrollbar}
							onMouseEnter={() => {
								if (resultsVisible) {
									playSound(Sound.LinkCursor);
								}
							}}
						>
							<StyledSelectItem>
								<Text>{item}</Text>
							</StyledSelectItem>
						</StyledSelectItemWrapper>
					))}
				</StyledItemsContainer>
			</CSSTransition>
			{resultsVisible && (
				<StyledBackWrapper onClick={() => setResultsVisible(false)} />
			)}
		</StyledContainer>
	);
};

export { Select };
